import { useEffect, useMemo, useState } from "react";
import { MessageFragment, MessageProfileFragment } from "gql/graphql";
import { useAuth } from "modules/providers/auth-provider";
import colors from "app/colors";
import { useGlobalState } from "modules/providers/global-state-provider";
import moment from "moment";
import useQueryString from "./use-query-string";

interface IProps {
  message?: MessageFragment | MessageProfileFragment;
}

interface MessageBackground {
  backgroundColor: string;
  transition?: string;
}

//This timeout is needed because the thread appears to re-render so we need a buffer to make sure that the user sees the highlight
const HIGHLIGHT_TIMEOUT_SECONDS = 2;

const useMessageBackground = ({ message }: IProps) => {
  const { me } = useAuth();
  const { qsMessageId } = useQueryString();
  const { highlightMessage, setHighlightMessage } = useGlobalState();

  const _backgroundColor =
    message?.createdUserId === me?.id
      ? colors.messageBackgroundSelf
      : colors.messageBackground;

  const [messageBackground, setMessageBackground] = useState<MessageBackground>(
    {
      backgroundColor: _backgroundColor,
    },
  );

  const isHighlightTimedOut = useMemo(() => {
    if (!highlightMessage || highlightMessage.messageId !== message?.id) {
      return false;
    }
    return (
      moment().diff(highlightMessage.timestamp, "seconds") >
      HIGHLIGHT_TIMEOUT_SECONDS
    );
  }, [highlightMessage, message?.id]);

  useEffect(() => {
    if (isHighlightTimedOut || qsMessageId !== message?.id) {
      //to handle the scenario where the message goes from undefined to defined
      setMessageBackground({
        backgroundColor: _backgroundColor,
      });
      return;
    }
    setHighlightMessage({
      messageId: message?.id,
      timestamp: moment().toISOString(),
    });
    setMessageBackground({
      backgroundColor: colors.yellowLight,
    });
    const timer = setTimeout(() => {
      if (qsMessageId === message?.id) {
        setMessageBackground({
          backgroundColor: _backgroundColor,
          transition: "background-color 2s",
        });
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [
    _backgroundColor,
    message?.id,
    qsMessageId,
    isHighlightTimedOut,
    setHighlightMessage,
  ]);

  return {
    messageBackground,
  };
};

export default useMessageBackground;
